// FA204/FA207 / frank trap — THEME_MODES (a module-scope/sibling-export
// table) and normalizeThemeMode (a sibling-export function) are read bare by
// every other export below; both are unresolvable once this file's exports
// are individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers; readerVoice.js precedent). Every
// consumer below carries a self-contained local copy. THEME_MODES stays
// exported too — cross-file consumers (bootShell.js, cycleTheme.js,
// workspace/wireGlobalListeners.js) resolve it via a normal ESM import,
// unaffected by frank.
export const THEME_MODES = ['auto', 'light', 'dark']

export const normalizeThemeMode = (mode) => {
  const modes = ['auto', 'light', 'dark']
  return modes.includes(mode) ? mode : 'auto'
}

export const readStoredThemeMode = (storage = globalThis.localStorage) => {
  const modes = ['auto', 'light', 'dark']
  const normalize = (mode) => (modes.includes(mode) ? mode : 'auto')
  try {
    return normalize(storage?.getItem?.('themeMode'))
  } catch (_) {
    return 'auto'
  }
}

export const writeStoredThemeMode = (mode, storage = globalThis.localStorage) => {
  const modes = ['auto', 'light', 'dark']
  const normalized = modes.includes(mode) ? mode : 'auto'
  try {
    storage?.setItem?.('themeMode', normalized)
  } catch (_) {}
  return normalized
}

export const cycleThemeMode = (mode) => {
  const modes = ['auto', 'light', 'dark']
  const current = modes.includes(mode) ? mode : 'auto'
  const index = modes.indexOf(current)
  return modes[(index + 1) % modes.length]
}

// ── Per-USER persistence of the explicit theme choice (Nika 2026-08-16:
// "globalTheme AUTO by default — matching the system; if a user explicitly
// chooses, persist it and it wins over auto thereafter"). ────────────────
//
// Two stores, one truth:
//   • localStorage `themeMode` — the fast path this file already owns
//     (readStoredThemeMode / writeStoredThemeMode): available before auth,
//     applied at boot with no flash, cross-tab via the `storage` event.
//   • the user record — `user_preferences.themeMode` (root.userPrefs, the
//     per-user flat prefs row; `workspaceProject.userPreferences · update`
//     REPLACES the whole row, so every write spreads the current row — same
//     contract navbarTabMenu.js documents). Cross-device, survives a cleared
//     browser, and it is where per-user state belongs.
// Precedence: the record wins once it has loaded (an explicit choice made on
// any device); until then the local cache paints. A local choice with no
// record yet migrates UP on first reconcile. `'auto'` is a legitimate
// explicit choice too (a user can go back to following the OS) — it is
// stored like the others; the DEFAULT for a user who never chose is the
// config's `globalTheme: 'auto'` (packages/workspace/config.js), never a
// literal here.
//
// Frank note: every helper is self-contained (no bare sibling-export reads),
// same rule as the rest of this file.
export const persistThemeModeToUser = async (root, sdk, mode) => {
  const modes = ['auto', 'light', 'dark']
  const normalized = modes.includes(mode) ? mode : 'auto'
  if (!root || !sdk || typeof sdk.execute !== 'function') return normalized
  const prev = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : {}
  if (prev.themeMode === normalized) return normalized
  const next = { ...prev, themeMode: normalized }
  if (typeof root.update === 'function') root.update({ userPrefs: next }, { preventFetch: true })
  else root.userPrefs = next
  try {
    await sdk.execute('workspaceProject.userPreferences', 'update', { payload: next })
  } catch (e) {
    console.warn('[themeMode] user_preferences themeMode save failed', e)
  }
  return normalized
}

// Boot-time reconcile, called once the user_preferences row has landed on
// `root.userPrefs`. PURE decision (no DOM, no framework import): returns
//   { apply: 'light'|'dark'|'auto' }  → the record holds a choice that differs
//                                       from what is applied; caller flips the
//                                       runtime theme + local cache to it
//   { migrate: mode }                 → no record choice, but the local cache
//                                       holds a non-default one; caller
//                                       persists it to the record
//   null                              → nothing to do
export const reconcileThemeModeFromPrefs = (currentMode, prefs, storedMode, tenantMode) => {
  const modes = ['auto', 'light', 'dark']
  const norm = (m) => (modes.includes(m) ? m : null)
  const recorded = norm(prefs && typeof prefs === 'object' ? prefs.themeMode : null)
  const current = norm(currentMode) || 'auto'
  if (recorded) {
    // ⚠️ A RECORDED 'auto' IS NOT AN EXPLICIT CHOICE. The account row carries
    // 'auto' for everyone who never picked a theme, and this function cannot
    // tell that apart from a deliberate "follow my OS" — the same ambiguity
    // this file's header already warns about for the LOCAL key.
    //
    // Without this branch the row that means "no preference" actively UNDID
    // the tenant default: boot seeded light for the workspace, this reconcile
    // saw recorded 'auto' !== current 'light', returned { apply: 'auto' }, and
    // `changeGlobalTheme('auto')` then stamped `data-theme` from the OS.
    // Measured on app.bellforge.eu with the OS in dark: the surface painted
    // light while `data-theme` read "dark" at 9.7s and 20.3s, and even an
    // explicit `?globalTheme=light` was overridden. So a tenant default could
    // never survive first paint on any dark-OS machine.
    //
    // Precedence, unchanged in spirit and now actually enforced:
    //   explicit light/dark  >  tenant default  >  'auto' / the OS.
    // An explicit light or dark still wins here, which is what keeps a user
    // who really chose a theme in control of their own account.
    if (recorded === 'auto' && (tenantMode === 'light' || tenantMode === 'dark')) return null
    return recorded !== current ? { apply: recorded } : null
  }
  const local = norm(storedMode)
  if (local && local !== 'auto') return { migrate: local }
  return null
}

/**
 * The tenant's boot theme, read from the host-keyed identity that the brand
 * resolver persists (`cacheWhitelabelBootIdentity` in
 * workspace/functions/whitelabelBrand.js).
 *
 * Synchronous and network-free ON PURPOSE — every consumer needs it before or
 * during first paint, and the brand lookup itself is bounded at 600ms so it
 * frequently has not landed yet. Returns null off a white-label host, on a
 * first-ever visit, or whenever storage is unavailable.
 *
 * One implementation, three callers (index.html's head script reads the same
 * key inline, since it runs before any module can be imported).
 */
export const readTenantBootThemeMode = (win = typeof window !== 'undefined' ? window : null) => {
  try {
    const store = win && win.localStorage
    const host = win && win.location ? win.location.hostname : ''
    if (!store || !host) return null
    const raw = store.getItem('wlBoot:' + host)
    const parsed = raw ? JSON.parse(raw) : null
    const mode = parsed && parsed.theme
    return mode === 'light' || mode === 'dark' ? mode : null
  } catch (_) {
    return null
  }
}

// WS-THEME-BOOT-WRONG-MODE-1 — a light-configured account LOADED DARK, then
// partly flipped to light on the first interaction. Root cause: `bootShell`
// seeds `themeMode`/`themeResolved` synchronously from localStorage + OS at
// first paint; the authoritative correction (this file's
// `reconcileThemeModeFromPrefs`, fed by the account's `user_preferences`
// row) only lands deep async, in `loadPrivateData.js`'s `_prefsChain`, well
// after first paint. `changeGlobalTheme` at that point flips `data-theme`
// correctly for CSS-reactive elements, but factory/`.case` values baked
// INLINE during the wrong-mode initial render are never revisited — the
// "partly" in the report. This is a RACE, not a missing value: any fix that
// only makes the correction more reactive still repaints late, and
// repainting late is the reported bug.
//
// ⚠️ readStoredThemeMode() ALWAYS normalizes through
// ['auto','light','dark'] — "nothing ever stored" and "explicitly chose
// auto" both read back as the string 'auto', indistinguishable once
// normalized. That distinction is exactly what tells boot whether this
// device is genuinely UNCERTAIN (its local cache disagrees with the
// signed-in account's cross-device preference — the real repro condition,
// not "any fresh profile": a profile whose account has no preference
// anywhere correctly boots to auto/OS with no bug) — so hasNoStoredThemeMode
// below reads the RAW key, before readStoredThemeMode's normalization
// erases that information. Do not "simplify" boot to a single normalized
// read — that silently restores this bug for anyone who tidies it later.
export const hasNoStoredThemeMode = (storage = globalThis.localStorage) => {
  // null = "cannot determine" (storage missing or getItem threw) — kept
  // distinct from `true` (raw key genuinely absent) on purpose: a caller
  // must not let "cannot read the raw key" fall into the waiting branch by
  // default (WS-THEME-BOOT-WRONG-MODE-1 condition 2). Only a literal `true`
  // may trigger the bounded wait below.
  if (!storage || typeof storage.getItem !== 'function') return null
  try {
    return storage.getItem('themeMode') == null
  } catch (_) {
    return null
  }
}

// Bounded pre-flight for the narrow uncertain case ONLY (hasNoStoredThemeMode
// === true) — every returning visit (raw key present, the overwhelming
// common case) skips this entirely: zero added latency, unchanged from
// today. `workspaceProject.userPreferences` is already fetched as necessary
// boot data later in `_prefsChain`; this only changes WHEN a result is
// acted on for a first-ever-boot device, not whether the request happens —
// it does not touch the returning-visit / page-switch path.
//
// ⛔ MUST stay bounded. Boot is making first paint depend on a network
// fetch; an unbounded wait turns a theme mismatch into a BLANK PAGE if the
// fetch is slow, fails, or the user is offline — trading a cosmetic bug for
// a total one, on the exact cold-boot path with no cached anything. On
// timeout OR any failure, fall through to the OS/auto guess and let the
// existing `_prefsChain` correction in loadPrivateData.js still apply as
// the safety net — this can only match or improve on today's behavior,
// never regress it.
//
// ⚠️ bootShell must call this AFTER its Pattern-B `sdk = getSDK()` — the
// workspace shell never passes `sdk` in, so a call placed before that
// assignment hits the `!sdk` guard every time and the pre-flight is dead
// code (the first shipped wiring did exactly that; pinned by
// bootShell.themeBootWiring.test.js).
// ⚡ BOOT-COST NOTE (NIKA-BATCH-DESIGN-PERF-1 F4-B, measured on PROD
// my.symbols.app 2026-08-22, visible tab, box GREEN): `/core/prefs/user`
// answered in 334 ms while this race is bounded at 300 ms, so the timer ALWAYS
// won and the pre-flight paid a flat 300 ms of dead boot wait and applied
// nothing. Worse, the `themeMode` raw key is only ever written by an EXPLICIT
// user choice, so a user who never toggled the theme is `rawKeyAbsent` on
// EVERY cold boot — the "one first-ever-boot device" cost the note above
// assumes is in fact a per-load cost, and the whole shell render then ran
// inside that late timer task.
// Fix: cache the SERVER's answer under its own key (`themeMode:serverHint`,
// value 'auto'|'light'|'dark'|'none'). It is deliberately NOT the `themeMode`
// key — writing that one would destroy the "never chose" vs "chose auto"
// distinction `hasNoStoredThemeMode` exists to protect. With a hint present the
// pre-flight resolves SYNCHRONOUSLY (waited: false, zero added latency) and the
// fetch still runs unawaited to refresh the hint, so a cross-device change is
// picked up on the next boot and, in the same load, by the existing
// `_prefsChain` correction in loadPrivateData.js — the unchanged safety net.
export const resolveBootThemeMode = async ({
  themeMode,
  rawKeyAbsent,
  sdk,
  signedIn,
  timeoutMs = 300,
  storage = globalThis.localStorage
}) => {
  if (!rawKeyAbsent || !sdk || typeof sdk.execute !== 'function') {
    return { mode: themeMode, waited: false }
  }
  // SIGNED-OUT GUARD (measured on app.bellforge.eu 2026-09-01). `/core/prefs/user`
  // is an AUTHED endpoint. On the /login screen there is no session, so this
  // pre-flight asked it anyway and took a 401 — once per cold boot, in a
  // network trace of 212 responses it was the only one — and then paid the
  // race timeout for an answer that could never arrive. A signed-out visitor
  // has no stored preference to fetch, so the whole pre-flight is dead work.
  // `signedIn` is the CALLER's verdict and only an explicit `false` skips:
  // every existing caller and test that omits it keeps today's behaviour, and
  // a wrong `false` costs nothing but the OS guess that the timeout arm
  // already returns (the `_prefsChain` correction in loadPrivateData.js stays
  // the safety net either way).
  if (signedIn === false) {
    return { mode: themeMode, waited: false, signedOut: true }
  }
  // Frank note (this file's rule): the decision is the SAME as
  // reconcileThemeModeFromPrefs(themeMode, prefs, themeMode) — inlined
  // rather than called, because a bare sibling-export read is an
  // unresolvable free identifier once this file's exports are individually
  // frank.toJSON'd (it would throw into the catch below and silently report
  // `errored: true` on every platform-delivered consumer). With stored ===
  // current the migrate branch can never fire, so only `apply` is modelled.
  // Same rule for the hint reader/writer below: inlined, never a sibling call.
  const modes = ['auto', 'light', 'dark']
  const norm = (m) => (modes.includes(m) ? m : null)
  const HINT_KEY = 'themeMode:serverHint'
  const readHint = () => {
    try {
      const raw = storage?.getItem?.(HINT_KEY)
      return raw === 'none' ? 'none' : norm(raw)
    } catch (_) {
      return null
    }
  }
  const writeHint = (value) => {
    try {
      storage?.setItem?.(HINT_KEY, value)
    } catch (_) {}
  }
  const hintOf = (prefs) => norm(prefs && typeof prefs === 'object' ? prefs.themeMode : null) || 'none'
  const current = norm(themeMode) || 'auto'
  const TIMED_OUT = Symbol('themeBootPrefsTimeout')

  // Fast path — a cached server answer means NO wait at all.
  const hint = readHint()
  if (hint) {
    try {
      Promise.resolve(sdk.execute('workspaceProject.userPreferences', 'get'))
        .then((prefs) => writeHint(hintOf(prefs)))
        .catch(() => {})
    } catch (_) {}
    if (hint !== 'none' && hint !== current) {
      return { mode: hint, waited: false, applied: true, fromHint: true }
    }
    return { mode: themeMode, waited: false, applied: false, fromHint: true }
  }

  // First boot on this device — the bounded race, unchanged, plus the hint write.
  let pending
  try {
    pending = Promise.resolve(sdk.execute('workspaceProject.userPreferences', 'get'))
  } catch (_) {
    return { mode: themeMode, waited: true, errored: true }
  }
  // Runs even when the timer wins the race, so the NEXT boot pays nothing.
  pending.then((prefs) => writeHint(hintOf(prefs))).catch(() => {})
  try {
    const prefs = await Promise.race([
      pending,
      new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), timeoutMs))
    ])
    if (prefs === TIMED_OUT) return { mode: themeMode, waited: true, timedOut: true }
    const recorded = norm(prefs && typeof prefs === 'object' ? prefs.themeMode : null)
    if (recorded && recorded !== current) return { mode: recorded, waited: true, applied: true }
    return { mode: themeMode, waited: true, applied: false }
  } catch (_) {
    return { mode: themeMode, waited: true, errored: true }
  }
}

// ── Layer 2: the WORKSPACE default theme ────────────────────────────────
// Nika 2026-08-19: "set 'globalTheme' 'light' for bellforge workspace by
// default - overriding users system preference", with precedence, highest
// first: (1) user workspace preference, (2) workspace preference, (3) system.
//
// Layers 1 and 3 already existed here (`user_preferences.themeMode` wins, and
// `'auto'` follows the OS). Layer 2 did NOT: the default for a user who never
// chose came from the build-time `config.globalTheme: 'auto'`, an app-wide
// constant with nowhere to say "this workspace is light and others stay auto".
// The workspace setting is `workspace.settings.theme = { mode }` — an object,
// not a bare string, so a later `accent`/`logo` can join without a migration.
//
// ⚠️ THE CASE THAT MAKES THIS SUBTLE, and the reason it is a pure function
// with its own tests: a user who EXPLICITLY chose `'auto'` must keep
// following their OS and must NOT fall through to the workspace value —
// otherwise a workspace silently overrides a deliberate user setting.
// `readStoredThemeMode()` normalizes "never stored" and "explicitly chose
// auto" to the same string, so the caller must pass the RAW-key answer
// (`hasNoStoredThemeMode`), not the normalized mode.
//
// Returns, for a caller that owns one workspace-driven override at a time:
//   { apply: 'light'|'dark' } → adopt the workspace's mode
//   { revert: true }          → drop a previously applied workspace mode and
//                               fall back to the user's own resolution
//   null                      → nothing to do
export const resolveWorkspaceThemeMode = ({
  userChoseExplicitly,
  workspaceMode,
  appliedWorkspaceMode
} = {}) => {
  const wsMode = workspaceMode === 'light' || workspaceMode === 'dark' ? workspaceMode : null
  const applied =
    appliedWorkspaceMode === 'light' || appliedWorkspaceMode === 'dark'
      ? appliedWorkspaceMode
      : null

  // The user's own choice always wins — including an explicit 'auto'.
  if (userChoseExplicitly) return applied ? { revert: true } : null

  if (wsMode) return wsMode === applied ? null : { apply: wsMode }
  return applied ? { revert: true } : null
}

// Did this visitor ever make a theme choice of their own? Either store
// counts: the user record (cross-device, authoritative) or a raw
// localStorage key (this device). ⚠️ `rawStoredKeyAbsent` must come from
// `hasNoStoredThemeMode`, whose `true` means "genuinely never stored" —
// passing a normalized `'auto'` here would erase the very distinction this
// layer depends on.
export const userHasOwnThemeChoice = ({ rawStoredKeyAbsent, userRecordMode } = {}) => {
  const recorded =
    userRecordMode === 'auto' || userRecordMode === 'light' || userRecordMode === 'dark'
  if (recorded) return true
  return rawStoredKeyAbsent !== true
}

// ── Layer 2 at the BOOT SEED: the WORKSPACE record's default theme ─────────
// Nika 2026-10-18 (GreenIG, D1 option C): "Dark theme by default" — a
// workspace whose ground is dark in both themes (GreenIG's record darkens the
// canvas and the greys) opens in the dark theme, where its inks read.
//
// The setting is the one this file's Layer 2 header names:
// `workspace.settings.theme = { mode: 'light' | 'dark' }` on the workspace
// RECORD (a bare 'light' / 'dark' string is read too). Anything else — no
// settings (a list-shaped record), 'auto', a theme NAME — is no default: null.
export const workspaceThemeModeOf = (workspace) => {
  const settings = workspace && typeof workspace === 'object' ? workspace.settings : null
  const theme = settings && typeof settings === 'object' ? settings.theme : null
  const mode = theme && typeof theme === 'object' ? theme.mode : theme
  return mode === 'light' || mode === 'dark' ? mode : null
}

// The theme a boot SEEDS — every layer, pure. `baseMode` is the user's / the
// OS's answer (resolveBootThemeMode); the tenant layers only fill the gap where
// the user expressed nothing: the WORKSPACE record's default first (the more
// specific one), then the white-label BRAND's (the host's org). Precedence,
// highest first: the user's own choice → the workspace → the brand → the OS.
//
// ⚠️ A SEED, never a re-theme: bootResolve calls this only BEFORE create()
// (the warm mount from its snapshot's cached workspace default; the cold mount
// from the full record) — re-theming a MOUNTED app re-applies the design
// system across the whole tree, the bellforge freeze that parked
// workspaceConfigs.js `WORKSPACE_DEFAULT_THEME_ENABLED`. Never persisted: a
// tenant default is not a choice the user made.
// Frank note (this file's rule): self-contained, no sibling-export reads.
export const bootTenantThemeMode = ({
  baseMode,
  userChoseExplicitly,
  workspaceMode,
  brandMode
} = {}) => {
  const pick = (m) => (m === 'light' || m === 'dark' ? m : null)
  const base = ['auto', 'light', 'dark'].includes(baseMode) ? baseMode : 'auto'
  if (userChoseExplicitly) return base
  return pick(workspaceMode) || pick(brandMode) || base
}
